import { useState, type CSSProperties } from 'react';
import { photoFor } from '@/data/team';

type Props = {
  /** Team member's name — used to look up their headshot. */
  name: string;
  /** Shown until a photo loads, and kept permanently if there isn't one. */
  initials: string;
  /** Omit to let the caller's CSS size the circle (e.g. Conncct Private's `.ph`). */
  size?: number;
  className?: string;
  style?: CSSProperties;
};

/**
 * A headshot that degrades to initials.
 *
 * The photos are hosted in Storage (see data/team.ts), so a missing or slow file
 * must not leave a broken-image icon in the middle of a team section. If the file
 * isn't there, the initials stay — which is exactly what these sections showed
 * before, so the page looks intentional either way.
 */
export default function TeamAvatar({ name, initials, size, className, style }: Props) {
  const src = photoFor(name);
  const [failed, setFailed] = useState(false);

  const box: CSSProperties = {
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
    flex: 'none',
    ...(size ? { width: size, height: size } : {}),
    ...style,
  };

  return (
    <span className={className} style={box}>
      {src && !failed ? (
        <img
          src={src}
          alt={name}
          loading="lazy"
          onError={() => setFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
        />
      ) : initials}
    </span>
  );
}
